import { useState, FormEvent } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    projectType: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const WORKER_URL = process.env.NODE_ENV === 'production' 
        ? 'https://interface-research-contact.damp-tooth-b585.workers.dev'
        : 'http://localhost:8787'; // For local development with wrangler
      
      const response = await fetch(WORKER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          organization: '',
          projectType: '',
          message: ''
        });
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-4xl font-sans font-bold mb-6">Contact Us</h1>
      <p className="text-lg text-glam-secondary dark:text-gray-300 mb-12">
        Ready to transform your collection? Let's discuss how we can help you create 
        powerful digital solutions for your cultural institution.
      </p>

      <div className="grid md:grid-cols-2 gap-12">
        <div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold mb-2">
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                         bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-glam-accent"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold mb-2">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                         bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-glam-accent"
              />
            </div>

            <div>
              <label htmlFor="organization" className="block text-sm font-semibold mb-2">
                Organization
              </label>
              <input
                type="text"
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                         bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-glam-accent"
              />
            </div>

            <div>
              <label htmlFor="projectType" className="block text-sm font-semibold mb-2">
                Project Type
              </label>
              <select
                id="projectType"
                name="projectType"
                value={formData.projectType}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                         bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-glam-accent"
              >
                <option value="">Select a project type</option>
                <option value="collections">Collections Management</option>
                <option value="digitization">Digitization Project</option>
                <option value="knowledge">Knowledge Mining</option>
                <option value="platform">Full Platform Development</option>
                <option value="consulting">Consulting</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={6}
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                         bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-glam-accent"
                placeholder="Tell us about your project and goals..."
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-glam-primary text-white font-medium hover:bg-glam-secondary 
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {submitStatus === 'success' && (
              <div className="p-4 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-lg">
                Thank you for your message! We'll get back to you as soon as possible.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-lg">
                There was an error sending your message. Please try again or email us directly.
              </div>
            )}
          </form>
        </div>

        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-sans font-bold mb-4">Get in Touch</h2>
            <p className="text-glam-secondary dark:text-gray-300 mb-6">
              We work with cultural institutions worldwide to create custom digital 
              solutions. Whether you're planning a new project or looking to enhance 
              existing systems, we're here to help.
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-2">Email</h3>
            <p className="text-glam-secondary dark:text-gray-300">
              info@interface-research.net
            </p>
          </div>


          <div>
            <h3 className="font-semibold mb-2">Response Time</h3>
            <p className="text-glam-secondary dark:text-gray-300">
              We typically respond within 24 business hours
            </p>
          </div>

          <div className="bg-glam-light dark:bg-gray-900 p-6 rounded-lg">
            <h3 className="font-semibold mb-2">Schedule a Consultation</h3>
            <p className="text-glam-secondary dark:text-gray-300 mb-4">
              Prefer to discuss your project in person? We offer free 30-minute 
              consultations to understand your needs and explore possibilities.
            </p>
            <button className="px-8 py-4 border border-glam-primary text-glam-primary hover:bg-glam-primary hover:text-white transition-colors font-medium">
              Book a Meeting →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;